import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import Line from "../../component/ui/Line";
import gsap from "gsap";

const ImpactSection = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const stats = [
    {
      value: "240",
      unit: "metric tons per day",
      title: " Waste Processing Capacity",
      desc: "Organic waste treated with environmentally friendly methods.",
      color: "bg-[#B7DB83]", // Video jesa light green
    },
    {
      value: "11,000",
      unit: "standard cubic meters per day",
      title: "Green Gas Generation",
      desc: "High-purity renewable energy for various applications.",
      color: "bg-[#B7DB83]",
    },
    {
      value: "100",
      unit: "metric tons per day",
      title: "Organic Fertilizer Production (FOM)",
      desc: "Solid organic manure for improving soil health.",
      color: "bg-[#B7DB83]",
    },
    {
      value: "140",
      unit: "metric tons per day",
      title: " Liquid Organic Fertilizer Production (LFOM)",
      desc: "Nutrient-rich liquid delivery for sustainable farming.",
      color: "bg-[#B7DB83]",
    },
  ];
  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Text Reveal Animation
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".of-animated-text"),
      );
      textElements.forEach((element) => {
        gsap.set(element, {
          y: 25,
          // opacity: 0,

          clipPath: "inset(100% 0 0% 0)",
        });
        gsap.to(element, {
          y: 0,
          // opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: element.closest(".of-animate-trigger") || element,
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      });

      // Video Player Animation (Scale & Reveal)
      if (videoRef.current) {
        gsap.fromTo(
          videoRef.current,
          {
            scale: 0.9,
            // opacity: 0
          },
          {
            scale: 1,
            // opacity: 1,
            duration: 1.5,
            ease: "power3.out",
            scrollTrigger: {
              trigger: videoRef.current,
              start: "top 90%",
              end: "top 50%",
              scrub: 1,
            },
          },
        );
      }

      // Divider Line Animations
      const lineElements =
        sectionRef.current.querySelectorAll(".of-divider-line");
      lineElements.forEach((line) => {
        gsap.set(line, { scaleX: 0, transformOrigin: "left" });
        gsap.to(line, {
          scaleX: 1,
          scrollTrigger: {
            trigger: line.closest(".of-subtitle-wrapper") || line,
            start: "top 85%",
            scrub: 1,
          },
        });
      });

      // Card Animations
      const cardElements = sectionRef.current.querySelectorAll(".of-card");
      cardElements.forEach((card) => {
        gsap.set(card, {
          y: 40,
          // opacity: 0
        });
        gsap.to(card, {
          y: 0,
          // opacity: 1,
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            scrub: 1,
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);
  return (
    <section className="bg-[#F2F6EC] py-20 px-6 md:px-16 lg:px-24 font-[Sora]">
      <div className="max-w-[1612px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Left Side: Sticky Text */}

        <div className="lg:sticky lg:top-24">
          <div className="flex flex-row justify-start items-center w-full of-subtitle-wrapper overflow-hidden of-animate-trigger">
            <span className="text-[16px] 2xl:text-[24px] font-normal text-text-primary of-animated-text">
              Production
            </span>
            <Line
              className="ml-[15px] 2xl:ml-[30px] bg-text-primary of-divider-line"
              width="100px"
              height="2px"
            />
          </div>
          <h2 className="text-[30px] 2xl:text-[60px] font-bold text-text-primary of-animated-text mt-[20px]">
            Our Operational Capacity
          </h2>
          <p className="text-[16px] 2xl:text-[24px] font-normal leading-lg text-justify text-text-primary w-full of-animated-text mt-[20px]">
            Our operational capacity is driven by advanced waste processing
            technology, efficient resource management, and a commitment to
            sustainability. We specialize in converting organic waste into
            renewable energy and high-quality organic fertilizer through
            environmentally responsible methods. With scalable infrastructure
            and optimized production systems, we ensure consistent output,
            operational reliability, and compliance with environmental standards
            while contributing to a cleaner, circular economy.
          </p>
        </div>

        {/* Right Side: Animated Cards */}
        <div className="space-y-6 2xl:w-[70%] ml-auto">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`${stat.color} p-8 rounded-[2rem] shadow-sm hover:shadow-md transition-shadow`}
            >
              <div className="flex flex-col ">
                <span className="text-[30px] 2xl:text-[60px] font-black text-[#1C233A]">
                  {stat.value}
                </span>
                <span className="text-[16px] 2xl:text-[18px]  text-[#1C233A]  tracking-wider mb-4">
                  {stat.unit}
                </span>
                <h3 className="text-[20px] 2xl:text-[26px] font-bold text-[#1C233A] mb-2">
                  {stat.title}
                </h3>
                <p className="text-[#2D4A3E] text-[18px] 2xl:text-[24px] text-justify">
                  {stat.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;
